import { cn } from '@/lib/utils'
import { Controller, useFormContext } from 'react-hook-form'
import Card from '../Card'
import { Checkbox } from '../Checkbox'
import Grid from '../ui/Grid'
import { Label } from '../ui/label'
import type { CardRadioForm } from './CardRadioForm'

interface CardCheckboxOption<T> {
    value: T
    titulo: string
    descricao: string
}

interface CardCheckboxFormProps<T> {
    name: string
    options: CardCheckboxOption<T>[]
    label?: string
    required?: boolean
    disabled?: boolean
    spacing?: number
    /** Cards por linha a partir de `sm`; abaixo disso, sempre um. */
    colunas?: 1 | 2 | 3 | 4
}

/**
 * Versão de múltipla escolha do {@link CardRadioForm}: cada card alterna seu
 * `value` no array guardado em `name`.
 */
export function CardCheckboxForm<T>({
    name,
    options,
    label,
    required,
    disabled,
    spacing = 2,
    colunas = 2,
}: CardCheckboxFormProps<T>) {
    const { control } = useFormContext()
    const desabilitado = disabled || (control as any)._options?.disabled

    return (
        <Controller
            name={name}
            control={control}
            render={({ field, fieldState: { error } }) => {
                const selecionados: T[] = field.value ?? []

                const alternar = (valor: T) => {
                    if (desabilitado) return
                    field.onChange(
                        selecionados.includes(valor)
                            ? selecionados.filter(item => item !== valor)
                            : [...selecionados, valor],
                    )
                }

                return (
                    <div className="flex flex-col gap-4 mb-4">
                        {label && (
                            <Label>
                                {label}
                                {required && ' *'}
                            </Label>
                        )}

                        <Grid container spacing={spacing}>
                            {options.map(option => {
                                const selecionado = selecionados.includes(
                                    option.value,
                                )

                                return (
                                    <Grid
                                        key={String(option.value)}
                                        item
                                        xs={12}
                                        sm={12 / colunas}
                                        className="h-full"
                                    >
                                        <Card
                                            aria-disabled={desabilitado}
                                            className={cn(
                                                'flex-1 cursor-pointer transition-colors duration-200',
                                                'hover:bg-primary/10 select-none',
                                                {
                                                    'ring-2 ring-primary shadow-lg':
                                                        selecionado,
                                                    'opacity-50 cursor-not-allowed':
                                                        desabilitado,
                                                },
                                            )}
                                            onClick={() =>
                                                alternar(option.value)
                                            }
                                        >
                                            <div className="flex items-center gap-3">
                                                <Checkbox
                                                    checked={selecionado}
                                                    disabled={desabilitado}
                                                    tabIndex={-1}
                                                    className="pointer-events-none"
                                                />
                                                <h6 className="text-lg leading-5">
                                                    {option.titulo}
                                                </h6>
                                            </div>
                                            <p className="text-xs text-secondary-foreground mt-2 ml-8">
                                                {option.descricao}
                                            </p>
                                        </Card>
                                    </Grid>
                                )
                            })}
                        </Grid>

                        {error ? (
                            <span className="text-xs text-error">
                                {error.message}
                            </span>
                        ) : null}
                    </div>
                )
            }}
        />
    )
}
